import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import { JsonSchema } from '@sagold/react-json-editor';

import { DOC_UPLOAD_WITH_FILE_METADATA_EXTRACTION_KEY } from '../../utils/constants';

export const useGenerateSchemaDisablingMetaDataExtractionKey = (
  jsonSchema: JsonSchema,
  documentMetaDataExtraction?: FileUploadMetaDataExtractionDto
): JsonSchema => {
  const { t } = useTranslation();
  return useMemo(() => {
    if (
      !documentMetaDataExtraction?.enabled ||
      !documentMetaDataExtraction?.key
    ) {
      return jsonSchema;
    }

    const updatedSchema = structuredClone(jsonSchema);
    const propertyToDisable = documentMetaDataExtraction.key;
    const originalProperty = updatedSchema.properties?.[propertyToDisable];

    // if the doc view is configured to extract data from file-metadata but the user filter property is not defined,
    // then return orignal schema
    if (!originalProperty || typeof originalProperty !== 'object') {
      return jsonSchema;
    }

    if (updatedSchema.properties && updatedSchema.required) {
      updatedSchema.properties = Object.entries(
        updatedSchema.properties
      ).reduce(
        (acc, [key, value]) => {
          if (key === propertyToDisable && typeof value === 'object') {
            acc[key] = {
              ...value,
              // Only add minItems if it exists in originalProperty, done so to disable forced input validation
              ...(value.minItems !== undefined && { minItems: 0 }),
              // Disable the filed from the UI
              // if "required" is set as true in Json schema, the user will not be able to move forward since the filed is readOnly, so we set it to false.
              options: {
                ...value.options,
                disabled: true,
                required: false,
              },
            };
            acc[DOC_UPLOAD_WITH_FILE_METADATA_EXTRACTION_KEY] = {
              type: 'boolean',
              format: 'toggle',
              title: t('app.upload.extractMetaData.readFromDocument'),
              default: true,
              options: {
                readOnly: true,
                checked: true,
                classNames: ['read-from-file-name'],
                disabled: true,
              },
            };
            return acc;
          }
          acc[key] = value;
          return acc;
        },
        {} as { [key: string]: JsonSchema | boolean }
      );
      updatedSchema.required?.push(
        DOC_UPLOAD_WITH_FILE_METADATA_EXTRACTION_KEY
      );
    }

    return updatedSchema;
  }, [
    documentMetaDataExtraction?.enabled,
    documentMetaDataExtraction?.key,
    jsonSchema,
    t,
  ]);
};
